import { useAppBridge } from "@shopify/app-bridge-react";
import { createContext, useCallback, useContext } from "react";

const AuthContext = createContext(null);

/**
 * Auth Provider Component
 *
 * Provides Shopify App Bridge authentication to the component tree.
 * Must be rendered within Shopify's AppProvider (which provides the App Bridge instance).
 */
export function AuthProvider({ children }) {
  const shopify = useAppBridge();

  /**
   * Get the current session token from Shopify App Bridge
   *
   * The App Bridge automatically handles:
   * - Token generation
   * - Token refresh when expired
   * - Validation with Shopify
   */
  const getToken = useCallback(async () => {
    return await shopify.idToken();
  }, [shopify]);

  return (
    <AuthContext.Provider value={{ getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth Hook
 *
 * Access authentication methods from the Auth context.
 *
 * @throws Error if used outside of AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
